// One-click Brand Intelligence helpers shared by the brand rows (network
// Catalog > Brands, internal Brands cards): start a pack for a catalog brand
// and download an existing pack's ZIP by detail endpoint (extracted from
// PrepPackList.handleDownload so rows outside the list reuse the builder).
import toast from 'react-hot-toast';
import axiosInstant from '../../api';
import { PrepPack } from '../../pages/sales/prepPacks/types';
import { buildPrepPackZip, prepPackFileBase } from './prepPackFiles';
import { downloadBlob } from '../PdpAudit/reportFiles';

export type RunBrandIntelligenceResult = 'started' | 'already_generating' | 'no_product_urls' | 'rate_limited' | 'failed';

// Starts a pack for a catalog brand. Toasts every outcome; also RETURNS it,
// so a caller can show something that outlives a four-second toast (the
// brand card opens an "add a product" dialog on no_product_urls).
export async function runBrandIntelligence(
  endpoint: string,
  brandName: string,
  { silent = false }: { silent?: boolean } = {},
): Promise<RunBrandIntelligenceResult> {
  const say = (fn: () => void) => {
    if (!silent) fn();
  };
  try {
    await axiosInstant.post(endpoint);
    say(() => toast.success(`Sales Intel Prep Pack started for ${brandName} — it takes several minutes and lands in the Prep Pack column.`));
    return 'started';
  } catch (e: any) {
    const status = e?.response?.status;
    const code = e?.response?.data?.error || e?.response?.data;
    if (status === 409) {
      say(() => toast('A Sales Intel Prep Pack is already generating for this brand.'));
      return 'already_generating';
    }
    if (code === 'NoProductUrls') {
      say(() => toast.error(`${brandName} has no product pages in the catalog yet — add a product URL first.`));
      return 'no_product_urls';
    }
    if (status === 429) {
      say(() => toast.error("You've reached the limit for Sales Intel Prep Packs. Try again later."));
      return 'rate_limited';
    }
    say(() => toast.error("Couldn't start the Sales Intel Prep Pack. Please try again."));
    return 'failed';
  }
}

export async function downloadPrepPackById(detailEndpoint: string, { internal }: { internal: boolean }): Promise<void> {
  const toastId = toast.loading('Preparing download…');
  try {
    const { data: full } = await axiosInstant.get<PrepPack>(detailEndpoint);
    if (!full || !full.report) throw new Error('NoReport');
    const blob = await buildPrepPackZip(full, { internal });
    downloadBlob(blob, `${prepPackFileBase(full)}.zip`);
    toast.success('Report downloaded', { id: toastId });
  } catch {
    toast.error("Couldn't build the download. Please try again.", { id: toastId });
  }
}
